import { render } from "preact";
import { useState } from "preact/hooks";
import { type Signal, signal } from "@preact/signals";
import { type Face, faceUrl } from "../face.ts";
import { loadRecords, type RequestRecord } from "../records.ts";
import {
  accessTo,
  challengeFor,
  cleared,
  mastered,
  progressSummary,
  type RequestAccess,
  resumeOf,
} from "../progression.ts";
import type { RequestDef } from "../requests.ts";
import type { TargetDef } from "../targets.ts";
import { type Level, levelName, levelsOf } from "./board.tsx";
import { Icon } from "./icon.tsx";
import styles from "./ledger.module.css";

/** The ledger, opened by the game. */
export interface Ledger {
  /** Shows the ledger as recorded now; `onClose` runs when the player closes it. */
  open(onClose: () => void): Promise<void>;
  /** The way back, as 閉じる goes (null while it is not shown). */
  readonly wayBack: (() => void) | null;
}

/** The kitten's own face for its résumé's photo: the title kitten's fur, on a pale blue. */
const KITTEN_FACE: Face = {
  fur: "#f2b48c",
  pattern: "tabby",
  marks: ["#e39a6a"],
  wear: [],
  backdrop: "#dcebf4",
  square: true,
};

/**
 * The shop's ledger: for each request, how the deliveries have gone; and beside it, the
 * kitten's résumé.
 */
export function mountLedger(
  root: HTMLElement,
  requests: RequestDef[],
  targets: TargetDef[],
): Ledger {
  const records = signal<Map<string, RequestRecord> | null>(null);
  let onClose = () => {};
  const close = () => {
    records.value = null;
    onClose();
  };
  render(
    <LedgerScreen records={records} levels={levelsOf(requests, targets)} close={close} />,
    root,
  );
  return {
    async open(back) {
      onClose = back;
      try {
        records.value = await loadRecords();
      } catch {
        // No storage (a private window, say): nothing recorded.
        records.value = new Map();
      }
    },
    get wayBack() {
      return records.value ? close : null;
    },
  };
}

/** The card's two pages, as their tabs name them. */
const PAGES = [["ledger", "依頼帳"], ["resume", "こねこの履歴書"]] as const;

function LedgerScreen(
  { records, levels, close }: {
    records: Signal<Map<string, RequestRecord> | null>;
    levels: Level[];
    close: () => void;
  },
) {
  const [tab, setTab] = useState<(typeof PAGES)[number][0]>("ledger");
  const recorded = records.value;
  if (!recorded) return null;
  const all = levels.flatMap((l) => l.requests);
  const delivered = all.filter((r) => recorded.has(r.id)).length;
  return (
    <div class={styles.ledger}>
      {/* Two cards side by side on a wide screen; one at a time, by the tabs, if not. */}
      <div class={styles.card} data-tab={tab}>
        <div class={styles.tabs} role="tablist">
          {PAGES.map(([id, name]) => (
            <button
              key={id}
              type="button"
              role="tab"
              class={styles.tab}
              aria-selected={tab === id}
              onClick={() => setTab(id)}
            >
              {name}
            </button>
          ))}
        </div>
        <div class={styles.pages}>
          <div class={styles.page} data-page="ledger">
            <h2 class={styles.heading}>依頼帳</h2>
            <p class={styles.summary}>
              納品した依頼 <b>{delivered}</b> / {all.length}
            </p>
            <p class={styles.summary}>{progressSummary(recorded)}</p>
            {levels.map(({ holes, requests }) => (
              <section key={holes} class={styles.level}>
                <p class={styles.levelName}>{levelName(holes)}</p>
                <ul class={styles.entries}>
                  {requests.map((r) => (
                    <Entry
                      key={r.id}
                      request={r}
                      record={recorded.get(r.id)}
                      access={accessTo(r.id, recorded)}
                    />
                  ))}
                </ul>
              </section>
            ))}
          </div>
          <div class={styles.page} data-page="resume">
            <h2 class={styles.heading}>こねこの履歴書</h2>
            <ResumePage records={recorded} />
          </div>
        </div>
        <div class={`row ${styles.actions}`}>
          <button type="button" class="reset" onClick={close}>閉じる</button>
        </div>
      </div>
    </div>
  );
}

/** A request's line in the ledger: its best delivery, or not yet. */
function Entry(
  { request, record, access }: {
    request: RequestDef;
    record?: RequestRecord;
    access: RequestAccess;
  },
) {
  if (!access.available) {
    return (
      <li class={`${styles.entry} ${styles.notYet}`} data-locked="true">
        <span class={styles.rank}>―</span>
        <div class={styles.what}>
          <span class={styles.title}>未解放</span>
        </div>
        <span class={styles.none}>{access.reason}</span>
      </li>
    );
  }
  return (
    <li class={record ? styles.entry : `${styles.entry} ${styles.notYet}`}>
      <span class={styles.rank} data-rank={record?.bestRank}>{record?.bestRank ?? "―"}</span>
      <div class={styles.what}>
        <span class={styles.title}>{challengeFor(request.id) ? "お題 · " : ""}{request.title}</span>
        {challengeFor(request.id) && (
          <span class={styles.client}>
            {cleared(record) ? "お題達成" : "お題未達成"}
            {mastered(request.id, record) ? " · 腕試し達成" : ""}
          </span>
        )}
        <span class={styles.client}>
          <img class={styles.face} src={faceUrl(request.face)} alt="" />
          {request.client}より
        </span>
      </div>
      {record
        ? (
          <div class={styles.numbers}>
            <span>
              最高 <b>{record.bestTotal}</b>点（<b>{record.bestStrokes}</b>手）
            </span>
            <span>
              納品 <b>{record.deliveries}</b>回
            </span>
          </div>
        )
        : <span class={styles.none}>まだ納品していません</span>}
      <span
        class={record?.wishMet ? `${styles.wish} ${styles.met}` : styles.wish}
        title={record?.wishMet ? "こだわりを叶えた" : "こだわりはまだ"}
      >
        こだわり: {request.wish.label}
      </span>
    </li>
  );
}

/** The kitten's résumé: its photo and title, how far things have come, and its badges. */
function ResumePage({ records }: { records: Map<string, RequestRecord> }) {
  const { title, next, rates, badges } = resumeOf(records);
  return (
    <div class={styles.resume}>
      <div class={styles.profile}>
        <dl class={styles.fields}>
          <div>
            <dt>氏名</dt>
            <dd class={styles.name}>こねこ</dd>
          </div>
          <div>
            <dt>所属</dt>
            <dd>ねこ商会 工房</dd>
          </div>
          <div>
            <dt>肩書き</dt>
            <dd class={styles.rankTitle}>{title}</dd>
          </div>
        </dl>
        <img class={styles.photo} src={faceUrl(KITTEN_FACE)} alt="こねこの証明写真" />
      </div>
      <p class={styles.summary}>
        {next
          ? `お題をあと${next.more}つ達成すると「${next.name}」に`
          : "いちばん上の肩書きです。おみごと！"}
      </p>
      <section>
        <p class={styles.levelName}>達成率</p>
        <ul class={styles.rates}>
          {rates.map(({ label, done, of }) => (
            <li key={label} class={styles.rate}>
              <span>{label}</span>
              <b>
                {done} / {of}
              </b>
              <span class={styles.bar}>
                <span style={{ width: `${(100 * done) / of}%` }} />
              </span>
            </li>
          ))}
        </ul>
      </section>
      <section>
        <p class={styles.levelName}>
          称号 {badges.filter((b) => b.earned).length} / {badges.length}
        </p>
        <ul class={styles.badges}>
          {badges.map(({ name, how, earned }) => (
            <li key={name} class={earned ? `${styles.badge} ${styles.earned}` : styles.badge}>
              <span class={styles.medal}>
                <Icon name="medal" />
              </span>
              <b>{earned ? name : "？？？"}</b>
              <small>{how}</small>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
